// Copyright © 2026 Vale. All Rights Reserved.
// Soft "Install Vale on your phone" banner.
//
// Behaviour:
//  • Chrome / Edge / Android → intercepts `beforeinstallprompt`, offers a real
//    Install button that fires the native install prompt.
//  • iOS Safari (no beforeinstallprompt) → shows Share → Add to Home Screen copy.
//  • Hides forever once dismissed (localStorage) OR if the app is already
//    running as a PWA (`display-mode: standalone`).
//  • Shows on phone widths (<=768px), and on iPads at any width — desktop users
//    have real docks.
//  • Uses the app's shell palette (cream + calm), never toxic-positive copy.

import { useEffect, useRef, useState } from "react";
import { X, Smartphone, Share } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Link, useLocation } from "react-router-dom";
import { isIos } from "@/lib/device";

const STORAGE_KEY = "vale.pwa.installPromptDismissedAt";
const REMIND_AFTER_DAYS = 14;

const isStandalone = () => {
  if (typeof window === "undefined") return false;
  return (
    window.matchMedia?.("(display-mode: standalone)").matches ||
    // iOS Safari legacy
    window.navigator.standalone === true
  );
};

const isMobile = () => {
  if (typeof window === "undefined") return false;
  return window.matchMedia?.("(max-width: 768px)").matches;
};

const wasDismissedRecently = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return false;
    const at = Number(raw);
    if (!at) return false;
    const daysSince = (Date.now() - at) / 86_400_000;
    return daysSince < REMIND_AFTER_DAYS;
  } catch {
    return false;
  }
};

export function InstallAppPrompt() {
  const [visible, setVisible] = useState(false);
  const [mode, setMode] = useState(null); // "chrome" | "ios"
  const deferredRef = useRef(null);
  const { t } = useTranslation();
  const { pathname } = useLocation();

  useEffect(() => {
    if (isStandalone()) return;
    // Phones, plus iPads (which are wider than a phone but can't install any other way).
    if (!isMobile() && !isIos()) return;
    if (wasDismissedRecently()) return;

    const onBeforeInstall = (e) => {
      e.preventDefault();
      deferredRef.current = e;
      setMode("chrome");
      setVisible(true);
    };

    window.addEventListener("beforeinstallprompt", onBeforeInstall);

    // iOS fallback — no beforeinstallprompt exists there. Give the app a beat
    // to hydrate so we don't cover the very first paint.
    const timer = window.setTimeout(() => {
      if (!deferredRef.current && isIos()) {
        setMode("ios");
        setVisible(true);
      }
    }, 2500);

    const onInstalled = () => {
      setVisible(false);
      try { localStorage.setItem(STORAGE_KEY, String(Date.now())); } catch {}
    };
    window.addEventListener("appinstalled", onInstalled);

    return () => {
      window.removeEventListener("beforeinstallprompt", onBeforeInstall);
      window.removeEventListener("appinstalled", onInstalled);
      window.clearTimeout(timer);
    };
  }, []);

  const dismiss = () => {
    setVisible(false);
    try { localStorage.setItem(STORAGE_KEY, String(Date.now())); } catch {}
  };

  const install = async () => {
    const deferred = deferredRef.current;
    if (!deferred) return;
    deferred.prompt();
    try {
      await deferred.userChoice;
    } catch {}
    deferredRef.current = null;
    setVisible(false);
    try { localStorage.setItem(STORAGE_KEY, String(Date.now())); } catch {}
  };

  // The install page already shows the full steps.
  if (!visible || pathname === "/install") return null;

  return (
    <div
      data-testid="pwa-install-banner"
      role="dialog"
      aria-labelledby="pwa-install-title"
      className={`fixed left-3 right-3 bottom-3 pwa-above-nav z-[60] sm:left-auto sm:right-4 sm:bottom-4 sm:max-w-sm ${
        mode === "ios" ? "" : "md:hidden"} animate-in slide-in-from-bottom duration-300`}
    >
      <div className="rounded-2xl border border-stone-200 bg-[#f7f2ea]/95 backdrop-blur px-4 py-3 shadow-[0_10px_30px_-12px_rgba(0,0,0,0.25)]">
        <div className="flex items-start gap-3">
          <div className="mt-0.5 h-9 w-9 shrink-0 rounded-xl bg-[var(--calm)]/15 flex items-center justify-center">
            <Smartphone className="h-5 w-5 text-[var(--calm)]" aria-hidden />
          </div>
          <div className="flex-1 min-w-0">
            <p
              id="pwa-install-title"
              className="text-[15px] leading-snug font-semibold text-stone-900"
            >
              {t("pwa.title")}
            </p>
            {mode === "chrome" ? (
              <p className="text-[13px] leading-snug text-stone-600 mt-0.5">
                {t("pwa.chromeBody")}
              </p>
            ) : (
              <p className="text-[13px] leading-snug text-stone-600 mt-0.5">
                <Share className="inline h-3.5 w-3.5 me-1 -mt-0.5" aria-hidden="true" />
                {t("pwa.iosBody")}{" "}
                <Link to="/install" onClick={dismiss} data-testid="pwa-install-howto"
                  className="underline underline-offset-2 text-stone-800">{t("install.showMe")}</Link>
              </p>
            )}

            {mode === "chrome" && (
              <div className="mt-2.5 flex items-center gap-2">
                <button
                  data-testid="pwa-install-accept-btn"
                  onClick={install}
                  className="rounded-full bg-stone-900 text-[#f5f0e8] text-[13px] px-4 py-1.5 hover:bg-stone-800 transition-colors"
                >
                  {t("pwa.install")}
                </button>
                <button
                  data-testid="pwa-install-later-btn"
                  onClick={dismiss}
                  className="rounded-full text-stone-600 text-[13px] px-3 py-1.5 hover:text-stone-900 transition-colors"
                >
                  {t("pwa.notNow")}
                </button>
              </div>
            )}
          </div>
          <button
            data-testid="pwa-install-dismiss-btn"
            onClick={dismiss}
            aria-label={t("pwa.dismiss")}
            className="p-1 -m-1 text-stone-400 hover:text-stone-700 transition-colors"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
